import type { Prayer } from "./types";

export const prayer: Prayer = {
  slug: "prayer-for-strength",
  title: "A prayer for strength — for the day you have nothing left",
  h1: "A prayer for strength",
  description:
    "A prayer for the middle of something long, four passages on where strength is said to come from, and why waiting is the verb in most of them.",
  targets: [
    "prayer for strength",
    "prayer when you feel weak",
    "prayer for endurance",
    "prayer to keep going",
  ],
  published: "2026-09-03",
  standfirst:
    "Most requests for strength are not about a single hard thing. They are about week eleven of something that has no end date.",
  takeaways: [
    "Ask for today's portion. Strength in Scripture is issued daily, like the manna, and asking for a year of it in advance has never been how it works.",
    "Isaiah 40:31 has a verb people skip: the strength is renewed by those who wait. Waiting is the activity, not the delay before it.",
    "2 Corinthians 12:9 is not saying weakness is good. It is saying the power arrives at the place where you ran out, which is a different claim.",
  ],
  when: [
    "On the morning of a day you already know will be too long.",
    "In week eleven, when the thing you were coping with has become the normal thing.",
    "When somebody has said you are handling it well and you know exactly how untrue that is.",
    "Before a conversation you have no energy left to have.",
  ],
  prayer: [
    "God, I am out. Not dramatically — I can still function, I am just running on nothing and I have been for a while.",
    "I am not asking to feel strong. I have stopped believing that is coming. I am asking for enough for today, and I will come back tomorrow for tomorrow's.",
    "Where I am carrying something that was never mine to carry, show me which part, and give me the nerve to put it down.",
    "Where it is mine, hold the other end of it, because I cannot do this stretch on my own.",
    "And do not let me confuse being tired with being finished. Those have felt identical for about a month.",
  ],
  short: "God, I am out. Enough for today, and I will come back tomorrow.",
  honest: [
    "This prayer does not shorten the thing you are in. Nothing in it asks for the situation to end, and a page that promised that would be lying to somebody who is too tired to argue with it.",
    "It also does not make exhaustion into a spiritual state. Sometimes what you need is sleep, a week off, or somebody else doing the shopping, and treating those as lesser answers is a good way to stay exhausted with a clear conscience.",
    "If the tiredness is not touched by rest at all — if a full night changes nothing for weeks on end — that is a medical question and it deserves a doctor rather than a longer prayer.",
  ],
  verses: [
    {
      verse: {
        text: "but those who wait for Yahweh will renew their strength. They will mount up with wings like eagles. They will run, and not be weary. They will walk, and not faint.",
        ref: "Isaiah 40:31",
      },
      note: "Read the order of the three images: mounting up, running, then walking. It descends, which is the opposite of how a motivational passage would build, and the last and least impressive one — walking and not fainting — is the climax. This was written to exiles a long way into a long thing, and it promises the kind of strength that keeps someone upright rather than the kind that makes them fly.",
    },
    {
      verse: {
        text: 'He has said to me, "My grace is sufficient for you, for my power is made perfect in weakness." Most gladly therefore I will rather glory in my weaknesses, that the power of Christ may rest on me.',
        ref: "2 Corinthians 12:9",
      },
      note: "The context matters more than usual here: Paul has asked three times for something to be removed and been told no. This is the answer he got instead of the one he wanted. It is not a claim that weakness is pleasant or that he stopped minding — it is what he was given in place of relief, and he is reporting it rather than recommending the condition.",
    },
    {
      verse: {
        text: "My flesh and my heart fails, but God is the strength of my heart and my portion forever.",
        ref: "Psalm 73:26",
      },
      note: "The first half is an admission with nothing softened about it: the body gives out and so does the resolve. The psalm gets there after seventeen verses of the writer being furious that dishonest people seem to be doing fine. Strength arrives in this psalm on the far side of envy and exhaustion, not instead of them.",
    },
    {
      verse: {
        text: "Don't be grieved, for the joy of Yahweh is your strength.",
        ref: "Nehemiah 8:10",
      },
      note: "Said to a crowd who had just heard the law read aloud for the first time in generations and had started weeping, because they could hear how far off it they were. The instruction that comes with it is startlingly physical — eat well, drink something sweet, and send food to anyone who has none. Joy here is not a mood summoned by effort; it is a day organised around a feast.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "Strength comes in day-sized portions",
      id: "strength-comes-in-day-sized-portions",
    },
    {
      type: "p",
      text: "The manna in Exodus could not be stored. Anything kept overnight went bad, and the point of the arrangement was that the people had to come back every morning. It is a strange system to design deliberately, and it is the pattern the rest of Scripture keeps using for strength.",
    },
    {
      type: "p",
      text: "Which means the exhaustion of looking at the next six months and concluding you cannot do it is not a spiritual failure. You are correct. You cannot do six months. You have not been given six months; you have been given today.",
    },
    {
      type: "h2",
      text: "Waiting is the verb",
      id: "waiting-is-the-verb",
    },
    {
      type: "p",
      text: "Isaiah 40:31 and Psalm 27:14 both make waiting the activity that produces the strength, which reads oddly in English because waiting sounds passive. The Hebrew is closer to the tension in a rope pulled taut — a held expectancy, not an empty afternoon.",
    },
    {
      type: "p",
      text: "Practically, that means the request is not to be made able to do this, but to be kept facing the right way while it happens. Those produce very different weeks.",
    },
    {
      type: "h2",
      text: "Put down what is not yours",
      id: "put-down-what-is-not-yours",
    },
    {
      type: "p",
      text: "A large share of tiredness is load that was never assigned. The outcome of somebody else's decision, the mood of a room, the responsibility for a person who has not asked you to take it. Asking for strength to keep carrying that is asking for help with the wrong problem.",
    },
    {
      type: "note",
      text: "A useful test: if you disappeared for a fortnight, which of the things you are carrying would simply stop existing, and which would be picked up by someone else? The second kind was never only yours.",
    },
    {
      type: "p",
      text: "And where the phone is part of the exhaustion — the evening spent recovering from a day by doing something that does not restore anything — the fix is closer to {rest} than to effort.",
      links: {
        rest: "/verses/bible-verses-about-rest",
      },
    },
  ],
  faq: [
    {
      q: "What is a good prayer for strength in hard times?",
      a: 'Ask for the day rather than the season: "God, I am out — enough for today, and I will come back tomorrow." That is the unit Scripture consistently issues strength in.',
    },
    {
      q: "Which Bible verse gives strength?",
      a: "Isaiah 40:31 is the best known, and worth reading to the end: it descends from flying to running to walking, and walking without fainting is where it lands. Psalm 73:26 is the one to reach for when the body has actually given out.",
    },
    {
      q: "Why do I still feel weak after praying for strength?",
      a: "Because the promise is rarely about feeling. 2 Corinthians 12:9 is the answer Paul got after asking three times for a problem to be removed and being refused — the strength arrived at the point he ran out, not before it.",
    },
  ],
  relatedPrayers: ["prayer-for-patience", "prayer-when-you-cant-pray"],
  related: ["digital-sabbath", "phone-addiction-bible-verses"],
  relatedTopics: ["bible-verses-about-strength", "bible-verses-about-rest"],
  relatedPassages: ["philippians-4-13", "isaiah-41-10"],
};
